import { FC, Fragment, useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import { Button, Col, Radio, Row, Spin } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import Icon from "@assets/icon_config";
import memberDetailsService from "@services/memberDetails.service";
import membersService from "@services/members.service";
import { IDataItem } from "../alternative-generic-medication";

import "./generic-drug.scss";
import { useAlternativeGenericMedicationStore } from "@stores/useAlternativeGenericMedicationStore";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";

interface IGetGenericDrugPopOverProps {
  index: number;
  showContentCount: number;
  setShowContentCount: any;
  showMoreState: boolean;
  setShowMoreState: any;
  setShowPopOverVisible: any;
}

const GetGenericDrugPopOver: FC<IGetGenericDrugPopOverProps> = ({ index, showContentCount, setShowContentCount, showMoreState, setShowMoreState, setShowPopOverVisible }) => {
  const [loading, setLoading] = useState(false);
  const [selectedRadioIndex, setSelectedRadioIndex] = useState(-1);
  const [isUntouched, setIsUntouched] = useState<boolean>(true);
  const dataState = useAlternativeGenericMedicationStore((state) => state.dataState);
  const setDataState = useAlternativeGenericMedicationStore((state) => state.setDataState);
  const [radioSate, setRadioState] = useState(cloneDeep(dataState));

  useEffect(() => {
    console.log("GetGenericDrugPopOver.useEffect");
    const newDataState = cloneDeep(dataState);
    setRadioState(newDataState);
    setIsUntouched(true);
  }, [dataState]);

  const handleCancelGenericDrug = () => {
    // revert to the old state
    setRadioState(dataState);
    setIsUntouched(true);
    setShowPopOverVisible(false);
  };

  const handleSaveGenericDrug = () => {
    setLoading(true);

    const tempRadioSate = cloneDeep(radioSate);
    let selectedCnt = 0;

    tempRadioSate[index].genericMedications.forEach((data) => {
      if (data.selected) {
        ++selectedCnt;
      }
    });

    // resetting generic option (+ Add Generic)
    if (selectedCnt === 0) {
      tempRadioSate[index].genericDrugBrand = null;
    }

    const cMember = membersService?.getCurrentMember;

    if (cMember && cMember?.patient_id) {
      const popupData = dataState[index].genericMedications[selectedRadioIndex];

      const genericDrugName = selectedCnt === 0 ? "" : popupData.genericDrugName;
      const genericBrand = selectedCnt === 0 ? "" : popupData.genericBrand;

      memberDetailsService.updateAlternateMedication(cMember.patient_id, tempRadioSate[index].medicationName, genericDrugName, genericBrand, false, false, false, dataState[index].ndcNumber ).subscribe({
        next: (v: any) => {
          memberDetailsService.getAlternateMedication(parseInt(cMember?.patient_id)).subscribe({
            next: (v: any) => {
              const markSelected: IDataItem[] = cloneDeep(v.data.data.medications.values);
              markSelected?.forEach((d) => {
                let tempGmArr: any = [];
                d.genericMedications.forEach((gm) => {
                  if (d.genericDrugBrand === gm.genericBrand && d.genericDrugName === gm.genericDrugName) {
                    gm["selected"] = true;
                    tempGmArr.unshift(gm);
                  } else {
                    gm["selected"] = false;
                    tempGmArr.push(gm);
                  }
                });
                d.genericMedications = tempGmArr;
              });
              // updating main state
              setDataState(markSelected);
              setLoading(false);
            },
            error: (e) => {
              console.log(e);
            },
          });
        },
        error: (e) => {
          // on API fail revert changes
          setDataState(dataState);
          setLoading(false);
          console.log(e);
        },
      });
    }
  };

  return (
    <Fragment>
      <Spin spinning={loading} indicator={<HealthemSpinner/>}>
        <Row style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end", paddingBottom: "12px" }}>
          <div style={{ display: "flex", flexBasis: "100px", justifyContent: "space-between" }}>
            <Button
              disabled={isUntouched}
              onClick={() => {
                handleSaveGenericDrug();
              }}
              type="primary"
              style={{ height: "24px", fontSize: "16px", fontWeight: 600, display: "flex", alignItems: "center" }}
            >
              Save
            </Button>
            <div
              onClick={() => {
                handleCancelGenericDrug();
              }}
              className="close-popover"
            >
              <Icon name={"close"} />
            </div>
          </div>
        </Row>

        <Row style={{ width: "650px" }}>
          <Col span={2}></Col>
          <Col span={11} style={{ fontSize: "14px", fontWeight: 500, padding: "8px" }}>
            Generic Drug
          </Col>
          <Col span={11} style={{ fontSize: "14px", fontWeight: 500, padding: "8px" }}>
            Generic Brand
          </Col>
        </Row>

        <div style={{ maxHeight: "250px", padding: "8px", overflow: "auto" }} className="custom-scroll-bar">
          {dataState[index].genericMedications.map((data, genericMedicationsIndex) => {
            if (showContentCount - 1 >= genericMedicationsIndex) {
              return (
                <Row key={genericMedicationsIndex}>
                  <Col span={2} style={{ padding: "8px" }}>
                    <Radio
                      checked={radioSate[index]?.genericMedications[genericMedicationsIndex]?.selected}
                      onClick={() => {
                        const newDataStateForRadio = cloneDeep(radioSate);

                        newDataStateForRadio[index].genericMedications.forEach((d, i) => {
                          if (genericMedicationsIndex === i) {
                            newDataStateForRadio[index].genericDrugBrand = dataState[index].genericMedications[genericMedicationsIndex].genericBrand;
                            newDataStateForRadio[index].genericMedications[i].selected = !newDataStateForRadio[index].genericMedications[genericMedicationsIndex].selected;

                            // setting Accepted/Rejected status to "" because we have changed generic drug
                            newDataStateForRadio[index].status = "";

                            if (dataState[index].genericMedications[genericMedicationsIndex].selected === newDataStateForRadio[index].genericMedications[genericMedicationsIndex].selected) {
                              // if user is unselecting and selecting the same generic option then disable save button
                              setIsUntouched(true);
                            } else {
                              setIsUntouched(false);
                            }
                            setSelectedRadioIndex(genericMedicationsIndex);
                          } else {
                            // setting all radio selected to false excluding the changed one
                            // to maintain only one selected item of radio
                            newDataStateForRadio[index].genericMedications[i].selected = false;
                          }
                        });

                        setRadioState(newDataStateForRadio);
                      }}
                    />
                  </Col>
                  <Col span={11} style={{ padding: "8px" }}>
                    {data.genericDrugName}
                  </Col>
                  <Col span={11} style={{ padding: "8px" }}>
                    {data.genericBrand}
                  </Col>
                </Row>
              );
            } else {
              return null;
            }
          })}
        </div>

        {showMoreState && dataState[index].genericMedications.length > 5 && (
          <Row style={{ padding: "8px" }}>
            <div style={{ color: "#0072E3", cursor: "pointer" }} onClick={() => {
              setShowMoreState(false);
              setShowContentCount(dataState[index].genericMedications.length);
            }}>
              <span style={{ marginRight: "0.5rem", cursor: "pointer" }}>
                <PlusOutlined />
              </span>

              <span style={{ cursor: "pointer" }}>
                Show More
              </span>
            </div>
          </Row>
        )}
      </Spin>
    </Fragment>
  );
};

export default GetGenericDrugPopOver;
